import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Calendar, Clock, ArrowUpRight, MapPin, FunnelSimple } from "@phosphor-icons/react";
import { differenceInDays, format } from "date-fns";
import { api } from "@/lib/api";
import EmptyState from "@/components/EmptyState";
import { SkeletonRows } from "@/components/Skeleton";

const WINDOWS = [
  { key: "all", days: 0 },
  { key: "d7", days: 7 },
  { key: "d30", days: 30 },
  { key: "d90", days: 90 },
];

export default function CfpPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [windowKey, setWindowKey] = useState("all");
  const [topics, setTopics] = useState([]);
  const [topic, setTopic] = useState("");
  const [country, setCountry] = useState("");
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    api.get("/meta/topics").then((r) => setTopics(r.data));
    api.get("/meta/countries").then((r) => setCountries(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 200 };
    const w = WINDOWS.find((x) => x.key === windowKey);
    if (w && w.days > 0) params.within_days = w.days;
    if (topic) params.topic = topic;
    if (country) params.country = country;
    api.get("/cfp", { params }).then((r) => setItems(r.data.items || [])).finally(() => setLoading(false));
  }, [windowKey, topic, country]);

  const byBand = useMemo(() => {
    const out = { soon: [], thisMonth: [], later: [] };
    items.forEach((c) => {
      const d = differenceInDays(new Date(c.cfp_deadline), new Date());
      if (d <= 7) out.soon.push(c);
      else if (d <= 30) out.thisMonth.push(c);
      else out.later.push(c);
    });
    return out;
  }, [items]);

  return (
    <div data-testid="cfp-page" className="px-6 md:px-10 lg:px-16 py-12 md:py-16">
      <div className="overline mb-3">{t("cfp.breadcrumb")}</div>
      <h1 className="font-display font-black text-5xl md:text-7xl tracking-[-0.04em] leading-[.92] mb-4">
        {t("cfp.title")}
      </h1>
      <p className="text-zinc-600 max-w-2xl text-base leading-relaxed">{t("cfp.intro")}</p>

      <div className="mt-12 border-t border-zinc-950 pt-6 grid md:grid-cols-12 gap-6 items-end">
        <div className="md:col-span-5">
          <label className="overline block mb-2">{t("cfp.window")}</label>
          <div className="flex flex-wrap gap-2">
            {WINDOWS.map((w) => (
              <button
                key={w.key}
                onClick={() => setWindowKey(w.key)}
                data-testid={`cfp-window-${w.key}`}
                className={(windowKey === w.key ? "btn-primary" : "btn-secondary") + " text-sm"}
                style={{ padding: ".4rem .9rem" }}
              >
                {t(`cfp.win_${w.key}`)}
              </button>
            ))}
          </div>
        </div>
        <FilterSelect label={t("common.topic")} value={topic} onChange={setTopic} options={topics} testid="cfp-filter-topic" allLabel={t("common.all")} />
        <FilterSelect label={t("common.country")} value={country} onChange={setCountry} options={countries} testid="cfp-filter-country" allLabel={t("common.all")} />
      </div>

      <div className="mt-5 text-xs font-mono text-zinc-500">
        {loading ? t("common.loading") : t("cfp.openCalls", { n: items.length })}
      </div>

      {loading && (
        <div className="mt-12"><SkeletonRows count={5} testid="cfp-skeleton" /></div>
      )}

      {!loading && (
        <div className="mt-12 space-y-14">
          <Band title={t("cfp.bandSoon")} subtitle={t("cfp.bandSoonSub")} accent="#DC2626" items={byBand.soon} t={t} />
          <Band title={t("cfp.bandMonth")} subtitle={t("cfp.bandMonthSub")} accent="#2563EB" items={byBand.thisMonth} t={t} />
          <Band title={t("cfp.bandLater")} subtitle={t("cfp.bandLaterSub")} accent="#52525B" items={byBand.later} t={t} />
        </div>
      )}
      {!loading && items.length === 0 && (
        <div className="mt-12">
          <EmptyState
            testid="cfp-empty"
            icon={<Calendar size={22} />}
            title={t("cfp.empty")}
            body={t("cfp.emptyBody")}
            action={{ label: t("cfp.emptyCta"), to: "/conferences" }}
          />
        </div>
      )}
    </div>
  );
}

function Band({ title, subtitle, accent, items, t }) {
  if (items.length === 0) return null;
  return (
    <section data-testid={`cfp-band-${title}`}>
      <div className="flex items-center gap-3 mb-5">
        <span className="block w-2 h-2 rounded-full" style={{ background: accent }} />
        <div>
          <h2 className="font-display font-bold text-2xl tracking-tight">{title}</h2>
          <div className="text-xs font-mono text-zinc-500">{subtitle}</div>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
        {items.map((c, i) => {
          const d = differenceInDays(new Date(c.cfp_deadline), new Date());
          return (
            <motion.div key={c.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }} className="relative bg-white group hover:bg-[#FAFAFA]">
              <Link to={`/conferences/${c.id}`} data-testid={`cfp-card-${c.id}`} className="block p-5 h-full">
                <div className="flex items-start justify-between gap-3">
                  <span className="tag-brand">{c.topic}</span>
                  <span className="font-mono text-xs tabular" style={{ color: accent }}>
                    {d <= 0 ? t("cfp.today") : t("cfp.daysLeft", { n: d })}
                  </span>
                </div>
                <h3 className="font-display font-semibold text-lg tracking-tight leading-tight mt-3 group-hover:text-blue-700 transition-colors line-clamp-2">{c.title}</h3>
                <div className="mt-3 text-xs text-zinc-500 font-mono space-y-1">
                  <div className="flex items-center gap-1.5"><Clock size={12} /> {t("conf.deadline", { date: format(new Date(c.cfp_deadline), "MMM d, yyyy") })}</div>
                  <div className="flex items-center gap-1.5"><Calendar size={12} /> {format(new Date(c.start_date), "MMM d")} – {format(new Date(c.end_date), "MMM d, yyyy")}</div>
                  <div className="flex items-center gap-1.5"><MapPin size={12} /> {c.city}, {c.country}</div>
                </div>
                {c.cfp_url && (
                  <div className="mt-4 h-[18px]" />
                )}
              </Link>
              {c.cfp_url && (
                <a
                  href={c.cfp_url}
                  target="_blank"
                  rel="noreferrer"
                  className="absolute bottom-5 start-5 inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-900 link-ul"
                  data-testid={`cfp-submit-${c.id}`}
                >
                  {t("conf.submissionPage")} <ArrowUpRight size={11} data-arrow="forward" />
                </a>
              )}
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

function FilterSelect({ label, value, onChange, options, testid, allLabel }) {
  return (
    <div className="md:col-span-3">
      <label className="overline block mb-2">{label}</label>
      <div className="relative">
        <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full appearance-none border-b border-zinc-300 bg-transparent py-2 pr-6 text-sm outline-none cursor-pointer focus:border-zinc-950">
          <option value="">{allLabel || "All"}</option>
          {options.map((o) => (<option key={o} value={o}>{o}</option>))}
        </select>
        <FunnelSimple size={12} className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none text-zinc-400 rtl:right-auto rtl:left-0" />
      </div>
    </div>
  );
}
